import { PageHeader } from '@/shared/components/PageHeader';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Edit, KeyRound, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';
import { JobInformationSection } from '../sections/JobInformationSection';
import { LogSection } from '../sections/LogSection';
import { PersonalDetailsSection } from '../sections/PersonalDetailsSection';
import type { ColorScheme, SectionKey, User, UserDetail } from '../types/user';
import { UserTabNavigation } from './UserTabNavigation';
import { DeactivateUserDialog } from './dialogs/DeactivateUserDialog';
import { EditUserDetailsDialog } from './dialogs/EditUserDetailsDialog';
import { ReactivateUserDialog } from './dialogs/ReactivateUserDialog';
import { SetPasswordDialog } from './dialogs/SetPasswordDialog';

interface UserDetailViewProps {
  userId: string;
  userDetail: UserDetail;
  user: User;
  onBack: () => void;
}

export function UserDetailView({ userId, userDetail, user, onBack }: UserDetailViewProps) {
  const [activeSection, setActiveSection] = useState<SectionKey>('personal');
  const [colorScheme, _setColorScheme] = useState<ColorScheme>('neutral');
  const [editOpen, setEditOpen] = useState(false);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);

  const isActive = user.status !== 'offline';
  const hasCognitoAccount = Boolean(userDetail.cognitoUsername || userDetail.workEmail);

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">
      <PageHeader
        title={user.name}
        subtitle={`${user.role || '—'} • ${user.team || '—'}`}
        icon={
          <Avatar className="w-14 h-14 bg-orange-600 text-white border-2 border-white dark:border-gray-800">
            <AvatarFallback className="bg-orange-600 text-white">
              {getInitials(user.name)}
            </AvatarFallback>
          </Avatar>
        }
        breadcrumbs={[
          { label: 'Users', href: '/users' },
          { label: user.name, current: true },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-border" onClick={() => setEditOpen(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit User Details
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => setSetPasswordOpen(true)}
              disabled={!hasCognitoAccount}
              title={hasCognitoAccount ? undefined : 'User has no Cognito account'}
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Set Password
            </Button>
            {isActive ? (
              <Button
                variant="outline"
                className="border-border text-red-600 hover:text-red-700 dark:text-red-400"
                onClick={() => setDeactivateOpen(true)}
              >
                <UserX className="w-4 h-4 mr-2" />
                Deactivate
              </Button>
            ) : (
              <Button
                variant="outline"
                className="border-border text-green-600 hover:text-green-700 dark:text-green-400"
                onClick={() => setReactivateOpen(true)}
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Reactivate
              </Button>
            )}
          </div>
        }
      />

      <div className="px-6">
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-2 -ml-2">
          ← Back to Users
        </Button>
      </div>

      <UserTabNavigation activeSection={activeSection} onSectionChange={setActiveSection} />

      <div className="flex-1 overflow-y-auto bg-gradient-to-b from-muted/20 to-background">
        <div>
          {activeSection === 'personal' && (
            <PersonalDetailsSection
              user={user}
              colorScheme={colorScheme}
              userId={userId}
              userDetail={userDetail}
            />
          )}
          {activeSection === 'job' && (
            <JobInformationSection
              user={user}
              colorScheme={colorScheme}
              userId={userId}
              userDetail={userDetail}
            />
          )}
          {activeSection === 'log' && <LogSection user={user} userId={userId} />}
        </div>
      </div>

      <EditUserDetailsDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        userId={userId}
        user={userDetail}
      />
      <SetPasswordDialog
        open={setPasswordOpen}
        onOpenChange={setSetPasswordOpen}
        userId={userId}
        userName={user.name}
      />
      <DeactivateUserDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        userId={userId}
        userName={user.name}
      />
      <ReactivateUserDialog
        open={reactivateOpen}
        onOpenChange={setReactivateOpen}
        userId={userId}
        userName={user.name}
      />
    </div>
  );
}
